import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { collection, getDocs, doc, onSnapshot } from 'firebase/firestore';
import { StoreContext } from '../contexts/StoreContext';
import { useDisplayPreferences } from '../contexts/DisplayPreferencesContext';
import { navItems } from '../constants/navItems';
import { MAX_CONTENT_WIDTH, BORDER_RADIUS, SPACING_TIGHT, SPACING_ELEMENT, SPACING_COMPACT, SPACING_COMPONENT, SPACING_BUTTON, SPACING_CARD, SPACING_CARD_BODY, SPACING_GROUP, SPACING_PAGE_LARGE } from '../constants/layout';
import { FONT_FOOTNOTE, FONT_CAPTION, FONT_BODY, FONT_HEADLINE } from '../constants/typography';
import { COLORS } from '../constants/colors';
import { groupCategories } from '../constants/settings';
import { useSocialMedia } from '../contexts/SocialMediaContext';
import { db } from '../firebase';

function NavigationBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { store, loading } = useContext(StoreContext);
  const { socialMedia } = useSocialMedia();
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  // Load cached logo preferences from localStorage
  const getCachedPreferences = () => {
    try {
      const cached = localStorage.getItem('navigationBarPreferences');
      if (cached) {
        return JSON.parse(cached);
      }
    } catch (error) {
      console.error('Error loading cached preferences:', error);
    }
    return { logoURL: null, logoSize: 48 }; // default size
  };

  const cachedPrefs = getCachedPreferences();
  const [logoURL, setLogoURL] = useState(cachedPrefs.logoURL);
  const [logoSize, setLogoSize] = useState(cachedPrefs.logoSize);
  const [logoLoaded, setLogoLoaded] = useState(false);
  const [showFallbackText, setShowFallbackText] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearchHovered, setIsSearchHovered] = useState(false);
  const [isCartHovered, setIsCartHovered] = useState(false);
  const [isPhoneHovered, setIsPhoneHovered] = useState(false);
  const searchInputRef = useRef(null);
  const logoFirstLoad = useRef(true);
  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      const isMobileDevice =
        window.innerWidth <= 768 ||
        /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      setIsMobile(isMobileDevice);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Close menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
    setIsSearchOpen(false);
  }, [location]);

  // Helper to see if a given path is "active"
  const isActive = (path) => {
    if (path === '/') {
      return (
        location.pathname === '/' ||
        location.pathname.startsWith('/product/')
      );
    }
    return location.pathname === path;
  };

  const linkStyle = (path, overrides = {}) => {
    const active = isActive(path);
    return {
      textDecoration: 'none',
      color: active
        ? 'var(--color-navbar-pill-text)'
        : 'var(--color-navbar-text)',
      fontWeight: '700',
      padding: `${SPACING_COMPACT} ${SPACING_CARD}`,
      borderRadius: BORDER_RADIUS,
      backgroundColor: active
        ? 'var(--color-navbar-pill-bg)'
        : 'transparent',
      transition: 'all 0.2s ease',
      display: 'block',
      ...overrides,
    };
  };

  const sidebarLinkStyle = (path, i, total) => ({
    ...linkStyle(path),
    marginBottom: '0',
    padding: `${SPACING_BUTTON} ${SPACING_CARD}`,
    width: '100%',
    boxSizing: 'border-box',
    borderBottom: i < total - 1 ? '1px solid var(--color-border)' : 'none',
  });

  const sectionHeaderStyle = {
    fontSize: FONT_FOOTNOTE,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    marginBottom: SPACING_BUTTON,
    paddingLeft: SPACING_CARD,
    color: 'var(--color-navbar-text)',
    opacity: 0.7,
  };

  const iconStyle = {
    fontSize: '20px',
    width: '24px',
    textAlign: 'center',
    flexShrink: 0,
  };

  const SidebarLabel = ({ icon, label }) => (
    <span style={{ display: 'flex', alignItems: 'center', gap: SPACING_BUTTON }}>
      {icon && <span className="material-icons" style={iconStyle}>{icon}</span>}
      {label}
    </span>
  );

  const PLATFORMS = [
    { key: 'facebook',  label: 'Facebook',  iconClass: 'fab fa-facebook-f',  baseUrl: 'https://facebook.com/' },
    { key: 'instagram', label: 'Instagram', iconClass: 'fab fa-instagram',   baseUrl: 'https://instagram.com/' },
    { key: 'threads',   label: 'Threads',   iconClass: 'fab fa-threads',     baseUrl: 'https://threads.net/@' },
    { key: 'tiktok',    label: 'TikTok',    iconClass: 'fab fa-tiktok',      baseUrl: 'https://tiktok.com/@' },
    { key: 'youtube',   label: 'YouTube',   iconClass: 'fab fa-youtube',     baseUrl: 'https://youtube.com/@' },
    { key: 'twitter',   label: 'X',         iconClass: 'fab fa-x-twitter',   baseUrl: 'https://x.com/' },
    { key: 'linkedin',  label: 'LinkedIn',  iconClass: 'fab fa-linkedin-in', baseUrl: 'https://linkedin.com/in/' },
    { key: 'pinterest', label: 'Pinterest', iconClass: 'fab fa-pinterest-p', baseUrl: 'https://pinterest.com/' },
  ];

  const sidebarSocialLinks = socialMedia
    ? PLATFORMS
        .filter(p => socialMedia[p.key]?.trim())
        .map(p => {
          const value = socialMedia[p.key].trim();
          return {
            href: value.startsWith('http') ? value : `${p.baseUrl}${value}`,
            label: p.label,
            iconClass: p.iconClass,
          };
        })
    : [];

  const [isMenuHovered, setIsMenuHovered] = useState(false);

  const searchButtonStyle = {
    border: 'none',
    backgroundColor: isSearchHovered
      ? 'var(--color-navbar-pill-bg)'
      : 'transparent',
    color: isSearchHovered
      ? 'var(--color-navbar-pill-text)'
      : 'var(--color-navbar-icon)',
    borderRadius: '9999px',
    width: '36px',
    height: '36px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition:
      'background-color 0.2s ease, color 0.2s ease',
    flexShrink: 0,
  };

  // Phone number logic
  const phoneNumber = store?.storeContact?.phoneNumber?.trim();
  const phoneHref = phoneNumber ? `tel:${phoneNumber.replace(/[^+\d]/g, '')}` : '';
  const hasValidPhone = !!phoneNumber && !!phoneHref;

  const phoneButtonStyle = {
    border: 'none',
    backgroundColor: isPhoneHovered
      ? 'var(--color-navbar-pill-bg)'
      : 'transparent',
    color: isPhoneHovered
      ? 'var(--color-navbar-pill-text)'
      : 'var(--color-navbar-icon)',
    borderRadius: '9999px',
    padding: `${SPACING_ELEMENT} ${SPACING_BUTTON} ${SPACING_ELEMENT} ${SPACING_COMPACT}`,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: SPACING_ELEMENT,
    cursor: 'pointer',
    transition:
      'background-color 0.2s ease, color 0.2s ease',
    flexShrink: 0,
    textDecoration: 'none',
    whiteSpace: 'nowrap',
  };

  const cartButtonStyle = {
    border: 'none',
    backgroundColor: isCartHovered
      ? 'var(--color-navbar-pill-bg)'
      : 'transparent',
    color: isCartHovered
      ? 'var(--color-navbar-pill-text)'
      : 'var(--color-navbar-icon)',
    borderRadius: '9999px',
    width: '36px',
    height: '36px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition:
      'background-color 0.2s ease, color 0.2s ease',
    flexShrink: 0,
  };

  const menuButtonStyle = {
    border: 'none',
    backgroundColor: isMenuHovered
      ? 'var(--color-navbar-pill-bg)'
      : 'transparent',
    color: isMenuHovered
      ? 'var(--color-navbar-pill-text)'
      : 'var(--color-navbar-icon)',
    borderRadius: '9999px',
    width: '36px',
    height: '36px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition:
      'background-color 0.2s ease, color 0.2s ease',
    flexShrink: 0,
    padding: 0,
  };

  const iconSvgStyle = {
    width: '18px',
    height: '18px',
    display: 'block',
  };

  const trimmedSearch = searchTerm.trim();

  const filteredProducts = useMemo(() => {
    const term = trimmedSearch.toLowerCase();
    if (!term) {
      return [];
    }

    return products
      .filter(product => {
        const name = (product.productName || '').toLowerCase();
        const description = (product.description || '').toLowerCase();
        return name.includes(term) || description.includes(term);
      })
      .sort((a, b) => {
        const nameA = (a.productName || '').toLowerCase();
        const nameB = (b.productName || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });
  }, [products, trimmedSearch]);

  const searchResults = filteredProducts.slice(0, 20);
  const hasQuery = trimmedSearch.length > 0;
  const showNoResults = hasQuery && searchResults.length === 0;
  const showResultsSummary = hasQuery && !showNoResults;

  const handleSearchOpen = () => {
    setIsSearchHovered(false);
    setSearchTerm('');
    setIsSearchOpen(true);
  };

  const handleSearchClose = () => {
    setIsSearchOpen(false);
    setSearchTerm('');
    setIsSearchHovered(false);
  };

  const handleSearchSubmit = event => {
    event.preventDefault();
    if (searchResults.length === 0) {
      return;
    }
    const firstResult = searchResults[0];
    navigate(`/product/${firstResult.id}`);
    handleSearchClose();
  };

  useEffect(() => {
    if (!isSearchOpen) {
      return undefined;
    }

    const frame = requestAnimationFrame(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    });

    return () => cancelAnimationFrame(frame);
  }, [isSearchOpen]);

  useEffect(() => {
    if (!isSearchOpen) {
      return undefined;
    }

    const handleKeyDown = event => {
      if (event.key === 'Escape') {
        setIsSearchOpen(false);
        setSearchTerm('');
        setIsSearchHovered(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isSearchOpen]);

  // Only show the store name once it's loaded and available
  const storeTitle = !loading && store?.name ? store.name : '';
  const brandLinkStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: SPACING_COMPACT,
    textDecoration: 'none',
  };
  const storeTitleStyle = {
    color: 'var(--color-text-primary)',
    fontSize: '28px',
    fontFamily: 'var(--font-family-sans)',
    fontWeight: '900',
    textTransform: 'uppercase',
    letterSpacing: '0.02em',
  };
  const logoContainerStyle = {
    position: 'relative',
    width: `${logoSize}px`,
    height: `${logoSize}px`,
    transition: 'width 0.3s ease-in-out, height 0.3s ease-in-out',
  };
  const logoStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    width: '100%',
    objectFit: 'contain',
    opacity: logoLoaded ? 1 : 0,
    transition: 'opacity 0.4s ease-in-out',
  };
  const logoTextOverlayStyle = {
    ...storeTitleStyle,
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    whiteSpace: 'nowrap',
    opacity: showFallbackText && !logoLoaded ? 1 : 0,
    transition: 'opacity 0.4s ease-in-out',
    pointerEvents: 'none',
  };

  // Calculate navbar padding based on logo size
  const getNavPadding = () => {
    const paddingMap = {
      32: { mobile: 8, desktop: 10 },
      48: { mobile: 10, desktop: 12 },
      72: { mobile: 12, desktop: 16 },
    };
    return paddingMap[logoSize] || { mobile: 6, desktop: 8 };
  };

  const navPadding = getNavPadding();

  useEffect(() => {
    let isMounted = true;

    async function fetchCategories() {
      try {
        const snap = await getDocs(collection(db, 'categories'));
        if (!isMounted) return;
        const fetched = snap.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(cat => !!(cat?.title || cat?.name))
          .sort((a, b) => {
            const titleA = (a.title || a.name || '').toLowerCase();
            const titleB = (b.title || b.name || '').toLowerCase();
            return titleA.localeCompare(titleB);
          });
        setCategories(fetched);
      } catch (error) {
        if (isMounted) {
          setCategories([]);
        }
      }
    }

    fetchCategories();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    async function fetchProducts() {
      try {
        const snap = await getDocs(collection(db, 'products'));
        if (!isMounted) return;
        const fetched = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setProducts(fetched);
      } catch (error) {
        if (isMounted) {
          setProducts([]);
        }
      }
    }

    fetchProducts();

    return () => {
      isMounted = false;
    };
  }, []);

  // Listen to navigation bar preferences for dynamic logo
  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, 'navigation_bar_preferences', 'default'),
      (docSnapshot) => {
        let newLogoURL = null;
        let newLogoSize = 80; // default changed to 80

        if (docSnapshot.exists()) {
          const data = docSnapshot.data();
          newLogoURL = data?.logo_url || null;

          // Map logo_size string to pixel values
          const sizeMap = {
            small: 32,
            default: 48,
            large: 72,
          };
          const size = data?.logo_size || 'default';
          newLogoSize = sizeMap[size] || 48;

          // Dynamic favicon
          const faviconURL = data?.favicon_url;
          if (faviconURL) {
            let link = document.querySelector('link[rel="icon"]');
            if (!link) {
              link = document.createElement('link');
              link.rel = 'icon';
              document.head.appendChild(link);
            }
            link.href = faviconURL;
          }
        }

        // Update state
        setLogoURL(newLogoURL);
        setLogoSize(newLogoSize);

        // Cache to localStorage
        try {
          localStorage.setItem('navigationBarPreferences', JSON.stringify({
            logoURL: newLogoURL,
            logoSize: newLogoSize,
          }));
        } catch (error) {
          console.error('Error caching preferences:', error);
        }
      },
      (error) => {
        console.error('Error fetching logo:', error);
      }
    );

    return () => unsubscribe();
  }, []);

  // Reset logo loaded state when URL changes (not size - that's just CSS)
  useEffect(() => {
    setLogoLoaded(false);
    setShowFallbackText(false);
    logoFirstLoad.current = true;
  }, [logoURL]);

  // Show fallback text only after 2s if logo hasn't loaded yet
  useEffect(() => {
    if (logoLoaded || !logoURL) return;
    const timer = setTimeout(() => setShowFallbackText(true), 2000);
    return () => clearTimeout(timer);
  }, [logoLoaded, logoURL]);

  // Helper function to lighten a hex color
  const lightenColor = (hex, percent) => {
    // Remove # if present
    hex = hex.replace('#', '');

    // Convert to RGB
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    // Lighten
    const newR = Math.min(255, Math.floor(r + (255 - r) * percent));
    const newG = Math.min(255, Math.floor(g + (255 - g) * percent));
    const newB = Math.min(255, Math.floor(b + (255 - b) * percent));

    // Convert back to hex
    return '#' + ((1 << 24) + (newR << 16) + (newG << 8) + newB).toString(16).slice(1);
  };

  // Helper function to darken a hex color
  const darkenColor = (hex, percent) => {
    // Remove # if present
    hex = hex.replace('#', '');

    // Convert to RGB
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    // Darken
    const newR = Math.max(0, Math.floor(r * (1 - percent)));
    const newG = Math.max(0, Math.floor(g * (1 - percent)));
    const newB = Math.max(0, Math.floor(b * (1 - percent)));

    // Convert back to hex
    return '#' + ((1 << 24) + (newR << 16) + (newG << 8) + newB).toString(16).slice(1);
  };

  // Helper function to convert hex to rgba
  const hexToRgba = (hex, alpha) => {
    hex = hex.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  // Listen to brand colors preferences for dynamic theming
  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, 'brand_colors_preferences', 'default'),
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const data = docSnapshot.data();
          const root = document.documentElement;

          // Set base color and compute all derived colors
          if (data.brand_primary) {
            root.style.setProperty('--brand-primary', data.brand_primary);
            // Compute hover state (lightened by 15%)
            root.style.setProperty('--brand-primary-hover', lightenColor(data.brand_primary, 0.15));
            // Compute overlay (18% opacity)
            root.style.setProperty('--brand-accent-overlay', hexToRgba(data.brand_primary, 0.18));
            // Compute focus ring (25% opacity)
            root.style.setProperty('--brand-focus-ring', hexToRgba(data.brand_primary, 0.25));
            // Compute button color for light mode (darkened by 70%)
            const buttonColor = darkenColor(data.brand_primary, 0.70);
            root.style.setProperty('--brand-button-light', buttonColor);
            // Compute button hover (lightened from button color)
            root.style.setProperty('--brand-button-light-hover', lightenColor(buttonColor, 0.15));
          }

          // Cache to localStorage
          try {
            localStorage.setItem('brandColorsPreferences', JSON.stringify(data));
          } catch (error) {
            console.error('Error caching brand colors:', error);
          }
        }
      },
      (error) => {
        console.error('Error fetching brand colors:', error);
      }
    );

    return () => unsubscribe();
  }, []);

  // Load cached brand colors on mount
  useEffect(() => {
    try {
      const cached = localStorage.getItem('brandColorsPreferences');
      if (cached) {
        const data = JSON.parse(cached);
        const root = document.documentElement;

        // Set base colors and compute all derived colors
        if (data.brand_primary) {
          root.style.setProperty('--brand-primary', data.brand_primary);
          root.style.setProperty('--brand-primary-hover', lightenColor(data.brand_primary, 0.15));
          root.style.setProperty('--brand-accent-overlay', hexToRgba(data.brand_primary, 0.18));
          root.style.setProperty('--brand-focus-ring', hexToRgba(data.brand_primary, 0.25));
          const buttonColor = darkenColor(data.brand_primary, 0.70);
          root.style.setProperty('--brand-button-light', buttonColor);
          root.style.setProperty('--brand-button-light-hover', lightenColor(buttonColor, 0.15));
        }
      }
    } catch (error) {
      console.error('Error loading cached brand colors:', error);
    }
  }, []);

  const { showContact, showFAQ } = useDisplayPreferences();

  // Filter out Products link if there are no products; hide contact/faq per display prefs
  const headerNavItems = navItems.filter(item =>
    (item.path !== '/products' || products.length >= 1) &&
    (item.path !== '/contact' || showContact) &&
    (item.path !== '/faq' || showFAQ)
  );

  // Sort categories based on groupCategories setting
  const sortedCategories = groupCategories
    ? categories // If grouping is on, use original alphabetical order
    : [...categories].sort((a, b) => {
        // If grouping is off, sort with Product and Clearance at the bottom
        const titleA = (a.title || a.name || '').trim();
        const titleB = (b.title || b.name || '').trim();

        // Clearance always goes to the very bottom
        if (titleA === 'Clearance') return 1;
        if (titleB === 'Clearance') return -1;

        // Product goes above Clearance but below everything else
        if (titleA === 'Product') return 1;
        if (titleB === 'Product') return -1;

        // Everything else is sorted alphabetically
        return titleA.toLowerCase().localeCompare(titleB.toLowerCase());
      });

  const sidebarMenuContent = (
    <>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {headerNavItems.map((item, i) => (
          <Link key={item.path} to={item.path} className="sidebar-link" style={sidebarLinkStyle(item.path, i, headerNavItems.length)}>
            <SidebarLabel icon={item.icon} label={item.label} />
          </Link>
        ))}
      </div>

      {sortedCategories.length > 0 && (
        <div style={{ paddingTop: SPACING_CARD, display: 'flex', flexDirection: 'column' }}>
          <span style={sectionHeaderStyle}>Categories</span>
          {sortedCategories.map((category, i) => {
            const label = category.title || category.name || 'Category';
            return (
              <Link key={category.id} to={`/category/${category.id}`} className="sidebar-link" style={sidebarLinkStyle(`/category/${category.id}`, i, sortedCategories.length)}>
                {label}
              </Link>
            );
          })}
        </div>
      )}

      {sidebarSocialLinks.length > 0 && (
        <div
          style={{
            paddingTop: SPACING_CARD,
            marginTop: 'auto',
            borderTop: '1px solid var(--color-border)',
          }}
        >
          {sidebarSocialLinks.map(link => (
            <a
              key={link.href}
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              className="sidebar-link"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: SPACING_BUTTON,
                padding: `${SPACING_BUTTON} ${SPACING_CARD}`,
                color: 'var(--color-navbar-text)',
                textDecoration: 'none',
                borderRadius: BORDER_RADIUS,
                transition: 'background-color 0.2s ease',
              }}
            >
              <i className={link.iconClass} style={iconStyle} />
              <span>{link.label}</span>
            </a>
          ))}
        </div>
      )}
    </>
  );

  const portalTarget = typeof document !== 'undefined' ? document.body : null;

  const searchOverlay = isSearchOpen && portalTarget
    ? createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Search products"
      onClick={handleSearchClose}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(15, 15, 15, 0.7)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        zIndex: 200,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: `${SPACING_PAGE_LARGE} ${SPACING_CARD} ${SPACING_PAGE_LARGE}`,
        boxSizing: 'border-box',
        minHeight: '100vh',
        overflowY: 'auto',
      }}
    >
      <div
        onClick={event => event.stopPropagation()}
        style={{
          backgroundColor: 'var(--color-surface)',
          color: 'var(--color-text-primary)',
          borderRadius: '20px',
          width: '100%',
          maxWidth: '640px',
          padding: SPACING_GROUP,
          boxShadow: '0 24px 48px rgba(15, 23, 42, 0.25)',
          border: '1px solid var(--color-border)',
          display: 'flex',
          flexDirection: 'column',
          gap: '0',
          maxHeight: 'calc(100vh - 160px)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: SPACING_BUTTON,
          }}
        >
          <label
            htmlFor="nav-search-input"
            style={{
              fontSize: FONT_CAPTION,
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'var(--color-text-secondary)',
            }}
          >
            Search our catalog
          </label>
          <button
            type="button"
            onClick={handleSearchClose}
            aria-label="Close search dialog"
            style={{
              border: 'none',
              background: 'transparent',
              color: 'var(--color-text-secondary)',
              cursor: 'pointer',
              borderRadius: '9999px',
              width: '36px',
              height: '36px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background-color 0.2s ease, color 0.2s ease',
              flexShrink: 0,
            }}
            onMouseEnter={event => {
              event.currentTarget.style.backgroundColor =
                'var(--color-surface-muted)';
              event.currentTarget.style.color = 'var(--color-navbar-text)';
            }}
            onMouseLeave={event => {
              event.currentTarget.style.backgroundColor = 'transparent';
              event.currentTarget.style.color = 'var(--color-text-secondary)';
            }}
          >
            <span style={{ fontSize: '1.5rem', lineHeight: 1 }}>×</span>
          </button>
        </div>
        <form
          onSubmit={handleSearchSubmit}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: SPACING_COMPACT,
          }}
        >
              <input
                id="nav-search-input"
                ref={searchInputRef}
                type="search"
                value={searchTerm}
                onChange={event => setSearchTerm(event.target.value)}
                placeholder="Search for corals, frags, livestock, and more"
                style={{
                  flex: 1,
                  padding: `${SPACING_BUTTON} ${SPACING_CARD}`,
                  borderRadius: '12px',
                  border: '1px solid var(--color-border)',
                  backgroundColor: 'var(--color-surface-muted)',
                  color: 'var(--color-text-primary)',
                  fontSize: FONT_BODY,
                  outline: 'none',
                  boxShadow: '0 0 0 2px transparent',
                  transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
                }}
                onFocus={event => {
                  event.target.style.boxShadow =
                    '0 0 0 3px var(--brand-focus-ring)';
                  event.target.style.borderColor = 'var(--color-accent)';
                }}
                onBlur={event => {
                  event.target.style.boxShadow = '0 0 0 2px transparent';
                  event.target.style.borderColor = 'var(--color-border)';
                }}
              />
              <button
                type="submit"
                style={{
                  border: 'none',
                  backgroundColor: 'var(--color-accent)',
                  color: 'var(--color-accent-text)',
                  padding: `${SPACING_BUTTON} ${SPACING_CARD_BODY}`,
                  borderRadius: '12px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'background-color 0.2s ease',
                  whiteSpace: 'nowrap',
                  height: '44px',
                }}
                onMouseDown={event => event.preventDefault()}
                onMouseEnter={event => {
                  event.currentTarget.style.backgroundColor =
                    'var(--color-accent-hover)';
                }}
                onMouseLeave={event => {
                  event.currentTarget.style.backgroundColor =
                    'var(--color-accent)';
                }}
              >
                Search
              </button>
          </form>
        <div
          style={{
            fontSize: FONT_HEADLINE,
            color: 'var(--color-text-secondary)',
            marginTop: SPACING_CARD,
          }}
        >
          {hasQuery && (
            <>
              {showNoResults
                ? `No products found for "${trimmedSearch}".`
                : showResultsSummary &&
                  `Showing ${searchResults.length} matching ${
                    searchResults.length === 1 ? 'item' : 'items'
                  }.`}
            </>
          )}
        </div>
        {searchResults.length > 0 && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: SPACING_COMPACT,
              overflowY: 'auto',
              marginTop: SPACING_CARD,
              marginRight: `-${SPACING_TIGHT}`,
              paddingRight: SPACING_TIGHT,
            }}
          >
            {searchResults.map(product => {
              const name = product.productName || 'View product';
              const rawDescription = (product.description || '').trim();
              const snippet =
                rawDescription.length > 140
                  ? `${rawDescription.slice(0, 137)}…`
                  : rawDescription;

              return (
                <Link
                  key={product.id}
                  to={`/product/${product.id}`}
                  onClick={handleSearchClose}
                  style={{
                    textDecoration: 'none',
                    border: '1px solid var(--color-border)',
                    borderRadius: '12px',
                    padding: `${SPACING_BUTTON} ${SPACING_CARD}`,
                    backgroundColor: 'var(--color-surface-muted)',
                    color: 'var(--color-text-primary)',
                    transition:
                      'background-color 0.2s ease, border-color 0.2s ease',
                    display: 'block',
                  }}
                  onMouseEnter={event => {
                    event.currentTarget.style.backgroundColor =
                      'var(--color-card-background-hover)';
                    event.currentTarget.style.borderColor =
                      'var(--color-accent)';
                  }}
                  onMouseLeave={event => {
                    event.currentTarget.style.backgroundColor =
                      'var(--color-surface-muted)';
                    event.currentTarget.style.borderColor =
                      'var(--color-border)';
                  }}
                >
                  <span
                    style={{
                      fontWeight: 600,
                      display: 'block',
                      marginBottom: snippet ? SPACING_TIGHT : 0,
                    }}
                  >
                    {name}
                  </span>
                  {snippet && (
                    <span
                      style={{
                        fontSize: FONT_FOOTNOTE,
                        color: 'var(--color-text-secondary)',
                        display: 'block',
                      }}
                    >
                      {snippet}
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>,
    portalTarget
  ) : null;

  if (isMobile) {
    return (
      <>
        {/* Mobile Navigation */}
        <nav
          style={{
            position: 'sticky',
            top: 0,
            left: 0,
            right: 0,
            backgroundColor: 'var(--color-navbar-surface)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            zIndex: 100,
            borderBottom: '1px solid var(--color-navbar-border)',
          }}
        >
          <div
            className="nav-safe-area"
            style={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: `${navPadding.mobile}px ${SPACING_CARD}`,
              maxWidth: MAX_CONTENT_WIDTH,
              margin: '0 auto',
              width: '100%',
              transition: 'padding 0.3s ease-in-out',
            }}
          >
            {/* Hamburger Menu Button - Left */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              style={menuButtonStyle}
              aria-label="Toggle menu"
              onMouseEnter={() => setIsMenuHovered(true)}
              onMouseLeave={() => setIsMenuHovered(false)}
              onFocus={() => setIsMenuHovered(true)}
              onBlur={() => setIsMenuHovered(false)}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-around',
                  width: '18px',
                  height: '18px',
                }}
              >
                <span
                  style={{
                    display: 'block',
                    height: '2px',
                    width: '100%',
                    backgroundColor: 'currentColor',
                    borderRadius: '1px',
                    transition: 'all 0.3s ease',
                    transform: isMenuOpen
                      ? 'rotate(45deg) translate(4px, 4px)'
                      : 'none',
                  }}
                />
                <span
                  style={{
                    display: 'block',
                    height: '2px',
                    width: '100%',
                    backgroundColor: 'currentColor',
                    borderRadius: '1px',
                    transition: 'all 0.3s ease',
                    opacity: isMenuOpen ? 0 : 1,
                  }}
                />
                <span
                  style={{
                    display: 'block',
                    height: '2px',
                    width: '100%',
                    backgroundColor: 'currentColor',
                    borderRadius: '1px',
                    transition: 'all 0.3s ease',
                    transform: isMenuOpen
                      ? 'rotate(-45deg) translate(5px, -5px)'
                      : 'none',
                  }}
                />
              </div>
            </button>

            {/* Logo - Center */}
            <Link
              to="/"
              style={{
                ...brandLinkStyle,
                position: 'absolute',
                left: '50%',
                transform: 'translateX(-50%)',
              }}
            >
              {logoURL ? (
                <div style={logoContainerStyle}>
                  {storeTitle && (
                    <span style={logoTextOverlayStyle}>{storeTitle}</span>
                  )}
                  <img
                    src={logoURL}
                    alt="Floor Janitorial logo"
                    style={logoStyle}
                    onLoad={() => {
                      if (logoFirstLoad.current) {
                        logoFirstLoad.current = false;
                        setLogoLoaded(true);
                      }
                    }}
                  />
                </div>
              ) : (
                storeTitle && (
                  <span style={storeTitleStyle}>{storeTitle}</span>
                )
              )}
            </Link>

            {/* Icon Buttons - Right */}
            <div style={{ display: 'flex', alignItems: 'center', gap: SPACING_COMPACT }}>
              {/* Temporarily commented out cart button
              <button
                type="button"
                onClick={() => navigate('/cart')}
                style={cartButtonStyle}
                aria-label="View shopping cart"
                onMouseEnter={() => setIsCartHovered(true)}
                onMouseLeave={() => setIsCartHovered(false)}
                onFocus={() => setIsCartHovered(true)}
                onBlur={() => setIsCartHovered(false)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={iconSvgStyle}
                  aria-hidden="true"
                  focusable="false"
                >
                  <circle cx="9" cy="21" r="1" />
                  <circle cx="20" cy="21" r="1" />
                  <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
                </svg>
              </button>
              */}

              {/* Search button disabled
              <button
                type="button"
                onClick={handleSearchOpen}
                style={searchButtonStyle}
                aria-label="Open product search"
                onMouseEnter={() => setIsSearchHovered(true)}
                onMouseLeave={() => setIsSearchHovered(false)}
                onFocus={() => setIsSearchHovered(true)}
                onBlur={() => setIsSearchHovered(false)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={iconSvgStyle}
                  aria-hidden="true"
                  focusable="false"
                >
                  <circle cx="11" cy="11" r="7" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </button>
              */}

              {hasValidPhone && (
                <a
                  href={phoneHref}
                  style={phoneButtonStyle}
                  aria-label="Call store"
                  onMouseEnter={() => setIsPhoneHovered(true)}
                  onMouseLeave={() => setIsPhoneHovered(false)}
                  onFocus={() => setIsPhoneHovered(true)}
                  onBlur={() => setIsPhoneHovered(false)}
                >
                  <span
                    className="material-icons"
                    style={{ fontSize: '20px' }}
                    aria-hidden="true"
                  >
                    phone
                  </span>
                  {!isMobile && <span style={{ fontSize: '14px', fontWeight: 600, whiteSpace: 'nowrap' }}>{phoneNumber}</span>}
                </a>
              )}
            </div>
          </div>
        </nav>

        {/* Mobile Menu Overlay */}
        {isMenuOpen && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 99,
              animation: 'fadeIn 0.2s ease',
            }}
            onClick={() => setIsMenuOpen(false)}
          />
        )}

        {/* Mobile Menu Panel (translucent background) */}
        <div
          className="sidebar-safe-area"
          style={{
            position: 'fixed',
            top: 0,
            left: isMenuOpen ? 0 : '-280px',
            width: '280px',
            height: '100vh',
            backgroundColor: 'var(--color-mobile-menu-surface)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            zIndex: 101,
            transition: 'left 0.3s ease',
            display: 'flex',
            flexDirection: 'column',
            paddingTop: SPACING_PAGE_LARGE, // Space for the nav bar
          }}
        >
          <div
            style={{
              padding: SPACING_CARD,
              overflowY: 'auto',
              height: '100%',
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column',
              gap: SPACING_CARD,
            }}
          >
            {sidebarMenuContent}
          </div>
        </div>

        {/* Add fadeIn animation */}
        <style jsx>{`
          @keyframes fadeIn {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }
          .sidebar-link:hover {
            background-color: rgba(128, 128, 128, 0.15) !important;
          }
        `}</style>
        {searchOverlay}
      </>
    );
  }

  // Desktop Navigation (hamburger + logo + search)
  return (
    <>
      <nav
        style={{
          position: 'sticky',
          top: 0,
          left: 0,
          right: 0,
          backgroundColor: 'var(--color-navbar-surface)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          zIndex: 100,
          borderBottom: '1px solid var(--color-navbar-border)',
        }}
      >
        <div
          className="nav-safe-area"
          style={{
            maxWidth: MAX_CONTENT_WIDTH,
            margin: '0 auto',
            padding: `${navPadding.desktop}px ${SPACING_CARD}`,
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            transition: 'padding 0.3s ease-in-out',
          }}
        >
          {/* Hamburger Menu - Left Side */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            style={menuButtonStyle}
            aria-label="Toggle menu"
            onMouseEnter={() => setIsMenuHovered(true)}
            onMouseLeave={() => setIsMenuHovered(false)}
            onFocus={() => setIsMenuHovered(true)}
            onBlur={() => setIsMenuHovered(false)}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-around',
                width: '18px',
                height: '18px',
              }}
            >
              <span
                style={{
                  display: 'block',
                  height: '2px',
                  width: '100%',
                  backgroundColor: 'currentColor',
                  borderRadius: '1px',
                  transition: 'all 0.3s ease',
                  transform: isMenuOpen
                    ? 'rotate(45deg) translate(4px, 4px)'
                    : 'none',
                }}
              />
              <span
                style={{
                  display: 'block',
                  height: '2px',
                  width: '100%',
                  backgroundColor: 'currentColor',
                  borderRadius: '1px',
                  transition: 'all 0.3s ease',
                  opacity: isMenuOpen ? 0 : 1,
                }}
              />
              <span
                style={{
                  display: 'block',
                  height: '2px',
                  width: '100%',
                  backgroundColor: 'currentColor',
                  borderRadius: '1px',
                  transition: 'all 0.3s ease',
                  transform: isMenuOpen
                    ? 'rotate(-45deg) translate(5px, -5px)'
                    : 'none',
                }}
              />
            </div>
          </button>

          {/* Logo - Center */}
          <Link
            to="/"
            style={{
              ...brandLinkStyle,
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
            }}
          >
            {logoURL ? (
              <div style={logoContainerStyle}>
                {storeTitle && (
                  <span style={logoTextOverlayStyle}>{storeTitle}</span>
                )}
                <img
                  src={logoURL}
                  alt="Floor Janitorial logo"
                  style={logoStyle}
                  onLoad={() => {
                    if (logoFirstLoad.current) {
                      logoFirstLoad.current = false;
                      setLogoLoaded(true);
                    }
                  }}
                />
              </div>
            ) : (
              storeTitle && (
                <span style={storeTitleStyle}>{storeTitle}</span>
              )
            )}
          </Link>

          {/* Icon Buttons - Right Side */}
          <div style={{ display: 'flex', alignItems: 'center', gap: SPACING_COMPACT }}>
            {/* Temporarily commented out cart button
            <button
              type="button"
              onClick={() => navigate('/cart')}
              style={cartButtonStyle}
              aria-label="View shopping cart"
              onMouseEnter={() => setIsCartHovered(true)}
              onMouseLeave={() => setIsCartHovered(false)}
              onFocus={() => setIsCartHovered(true)}
              onBlur={() => setIsCartHovered(false)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={iconSvgStyle}
                aria-hidden="true"
                focusable="false"
              >
                <circle cx="9" cy="21" r="1" />
                <circle cx="20" cy="21" r="1" />
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
              </svg>
            </button>
            */}

            {/* Search button disabled
            <button
              type="button"
              onClick={handleSearchOpen}
              style={searchButtonStyle}
              aria-label="Open product search"
              onMouseEnter={() => setIsSearchHovered(true)}
              onMouseLeave={() => setIsSearchHovered(false)}
              onFocus={() => setIsSearchHovered(true)}
              onBlur={() => setIsSearchHovered(false)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={iconSvgStyle}
                aria-hidden="true"
                focusable="false"
              >
                <circle cx="11" cy="11" r="7" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </button>
            */}

            {hasValidPhone && (
              <a
                href={phoneHref}
                style={phoneButtonStyle}
                aria-label="Call store"
                onMouseEnter={() => setIsPhoneHovered(true)}
                onMouseLeave={() => setIsPhoneHovered(false)}
                onFocus={() => setIsPhoneHovered(true)}
                onBlur={() => setIsPhoneHovered(false)}
              >
                <span
                  className="material-icons"
                  style={{ fontSize: '20px' }}
                  aria-hidden="true"
                >
                  phone
                </span>
                {!isMobile && <span style={{ fontSize: '14px', fontWeight: 600, whiteSpace: 'nowrap' }}>{phoneNumber}</span>}
              </a>
            )}
          </div>
        </div>
      </nav>

      {/* Desktop Menu Overlay */}
      {isMenuOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 99,
            animation: 'fadeIn 0.2s ease',
          }}
          onClick={() => setIsMenuOpen(false)}
        />
      )}

      {/* Desktop Menu Panel */}
      <div
        className="sidebar-safe-area"
        style={{
          position: 'fixed',
          top: 0,
          left: isMenuOpen ? 0 : '-320px',
          width: '320px',
          height: '100vh',
          backgroundColor: 'var(--color-mobile-menu-surface)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          zIndex: 101,
          transition: 'left 0.3s ease',
          display: 'flex',
          flexDirection: 'column',
          paddingTop: SPACING_PAGE_LARGE,
        }}
      >
        <div
          style={{
            padding: SPACING_CARD,
            overflowY: 'auto',
            height: '100%',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            gap: SPACING_CARD,
          }}
        >
          {sidebarMenuContent}
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        .sidebar-link:hover {
          background-color: rgba(128, 128, 128, 0.15) !important;
        }
      `}</style>
      {searchOverlay}
    </>
  );
}

export default NavigationBar;
